/**
 * @file order-chain-links.ts
 * @description Переходы из «Пути заказа»: узел → адрес в админке
 * (phase19.24-order-card-chain-design.md §4).
 *
 * Правило фазы: сущность с номером — ссылка на свою страницу; сущность без
 * номера (платёж, отгрузка, возврат) — deep-link во вкладку карточки с
 * подсветкой строки, а не мёртвый серый текст. Поэтому вид перехода приходит
 * с сервера (`link.kind`), а слаг магазина и адрес карточки знает только
 * клиент — он их и подставляет.
 *
 * Реестр видов перехода — запись в `RESOLVERS`: новый вид (например, будущая
 * страница платежа) добавляется строкой, а не веткой в компоненте узла.
 */

import type { OrderChainLinkDto } from "./order-chain";
import { isOrderTabKey, type OrderTabKey } from "../hooks/useOrderTabParam";

/** Что клиент знает про место, откуда идёт переход. */
export interface ChainLinkContext {
  /** Слаг магазина из URL; без него ссылок не строим. */
  slug: string | undefined;
  /** Заказ, чья карточка открыта — цель вкладочных и якорных переходов. */
  orderId: string;
}

/**
 * Якоря карточки заказа — реестр, а не строки по месту: id блока и цель
 * кнопки обязаны совпадать, иначе «Решить недостачу» скроллит в пустоту.
 * Новый якорный блок = запись здесь + `id={ORDER_CARD_ANCHOR.<ключ>}`.
 */
export const ORDER_CARD_ANCHOR = {
  /**
   * Блок F30 «Согласование доставки». С шага 5.2 блок живёт во вкладке
   * «Доставка», и ссылка ведёт туда (`ANCHOR_AS_TAB`), но ключ сохранён:
   * сервер по-прежнему отдаёт этот якорь у узла заявки.
   */
  agreement: "agreement",
  /** Блок «Сборка» во вкладке «Обзор» (`OrderPickingSection`). */
  picking: "picking",
} as const;

/** Адрес карточки заказа — общий корень вкладок и якорей. */
export function orderCardPath(ctx: ChainLinkContext): string {
  return `/shop/${ctx.slug}/orders/${ctx.orderId}`;
}

/**
 * Ссылка на вкладку карточки заказа с подсветкой строки.
 * `focusId` — id платежа / отгрузки / возврата: вкладка раскроет строку и
 * обведёт её на пару секунд (§4).
 */
export function orderTabHref(
  ctx: ChainLinkContext,
  tab: OrderTabKey,
  focusId?: string | null,
): string {
  const params = new URLSearchParams({ tab });
  if (focusId) params.set("focus", focusId);
  return `${orderCardPath(ctx)}?${params.toString()}`;
}

/**
 * Сообщение о неизвестном ключе вкладки — только в dev: на проде менеджеру от
 * него пользы нет, а разработчику расхождение с сервером видно сразу.
 */
function warnUnknownTab(tab: string): void {
  if (import.meta.env.DEV) {
    console.warn(
      `[order-chain] Узел «Пути заказа» ведёт на неизвестную вкладку «${tab}» — ссылка не построена. Проверьте реестр ORDER_TABS и ответ сервера.`,
    );
  }
}

/** Реестр видов перехода: `link.kind` → адрес. */
const RESOLVERS: {
  [K in OrderChainLinkDto["kind"]]: (
    link: Extract<OrderChainLinkDto, { kind: K }>,
    ctx: ChainLinkContext,
  ) => string | null;
} = {
  // Сервер отдаёт путь без магазина («/wms/picking/<id>») — слаг ставим здесь.
  route: (link, ctx) => {
    if (!ctx.slug || !link.path) return null;
    const path = link.path.startsWith("/") ? link.path : `/${link.path}`;
    return `/shop/${ctx.slug}${path}`;
  },
  // Ключ вкладки с сервера сверяем с реестром: неизвестный — не ссылка, а
  // расхождение контрактов. Молча открывать «Обзор» нельзя — менеджер уехал
  // бы не туда и решил, что документа нет (находка ревью 2026-09-21).
  tab: (link, ctx) => {
    if (!ctx.slug) return null;
    if (!isOrderTabKey(link.tab)) {
      warnUnknownTab(link.tab);
      return null;
    }
    return orderTabHref(ctx, link.tab, link.focusId);
  },
  // Якорь остаётся внутри карточки, если блок и правда на странице. Блок,
  // переехавший во вкладку (`#agreement` → «Доставка», шаг 5.2), отдаём
  // вкладочной ссылкой: якорь без блока молча скроллил бы в никуда.
  anchor: (link, ctx) => {
    if (!ctx.slug) return null;
    const tab = ANCHOR_AS_TAB[link.anchor];
    return tab ? orderTabHref(ctx, tab) : `${orderCardPath(ctx)}#${link.anchor}`;
  },
};

/**
 * Якоря, чей блок переехал во вкладку. Реестр, а не `if`: следующий переезд
 * блока — запись здесь, и все узлы «Пути заказа» продолжают работать без
 * правок на сервере.
 */
const ANCHOR_AS_TAB: Record<string, OrderTabKey> = {
  [ORDER_CARD_ANCHOR.agreement]: "delivery",
};

/**
 * Адрес узла цепочки; `null` — переход невозможен (нет слага или вида).
 * Узел без адреса рисуется текстом, а не «ссылкой в никуда».
 */
export function resolveChainLink(
  link: OrderChainLinkDto | null | undefined,
  ctx: ChainLinkContext,
): string | null {
  if (!link) return null;
  const resolver = RESOLVERS[link.kind] as
    | ((l: OrderChainLinkDto, c: ChainLinkContext) => string | null)
    | undefined;
  return resolver ? resolver(link, ctx) : null;
}
